export type FieldType = {
    name: string;
    displayInfo?: string;
    isHidden?: boolean;
};

// Only _id is required for updates
export const REQUIRED_UPDATE_FIELDS: FieldType[] = [{ name: '_id', displayInfo: '(identifiant unique du site)' }];

// ... existing code ...
const URBANTZ_SERVICES = [
    'needsInventoryBoissons',
    'needsInventorySnack',
    'needsInventoryNonAlimentaire',
    'needsCleaningMachineNECTA',
    'needsSmallExpiryDateInventory',
    'replaceCompleteInventoryByZeroInventory',
    'needsPunctualExpiryDateInventory',
] as const;

const WEEK_TYPES = ['evenWeek', 'oddWeek'] as const;
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const STOCK_CATEGORIES = ['dessert', 'main', 'smallBites', 'starter'] as const;

// Generate urbantz metadata display fields (for UI)
const urbantzDisplayFields: FieldType[] = URBANTZ_SERVICES.map((service) => ({
    name: `urbantzMetadata.${service}.[monday/tuesday/wednesday/thursday/friday].[evenWeek/oddWeek]`,
    displayInfo: '(true/false)',
}));

// Generate actual urbantz metadata fields (for CSV processing)
const urbantzActualFields: FieldType[] = WEEKDAYS.flatMap((day) =>
    WEEK_TYPES.flatMap((weekType) =>
        URBANTZ_SERVICES.map((service) => ({
            name: `urbantzMetadata.${service}.${day}.${weekType}`,
            displayInfo: '(true/false)',
            isHidden: true,
        })),
    ),
);

// Generate display fields for deliveryDays
const deliveryDaysDisplayField: FieldType = {
    name: 'deliveryDays.[sunday/monday/tuesday/wednesday/thursday/friday/saturday]',
    displayInfo: '(true/false)',
};

const deliveryDaysActualFields: FieldType[] = DAYS_OF_WEEK.map((day) => ({
    name: `deliveryDays.${day}`,
    displayInfo: '(true/false)',
    isHidden: true,
}));

// Generate display fields for freshReassortDays
const freshReassortDaysDisplayField: FieldType = {
    name: 'freshReassortDays.[sunday/monday/tuesday/wednesday/thursday/friday/saturday]',
    displayInfo: '(true/false)',
};

const freshReassortDaysActualFields: FieldType[] = DAYS_OF_WEEK.map((day) => ({
    name: `freshReassortDays.${day}`,
    displayInfo: '(true/false)',
    isHidden: true,
}));

// Generate display fields for dryReassortDays
const dryReassortDaysDisplayField: FieldType = {
    name: 'dryReassortDays.[sunday/monday/tuesday/wednesday/thursday/friday/saturday]',
    displayInfo: '(true/false)',
};

const dryReassortDaysActualFields: FieldType[] = DAYS_OF_WEEK.map((day) => ({
    name: `dryReassortDays.${day}`,
    displayInfo: '(true/false)',
    isHidden: true,
}));

// Generate display fields for reassortment targets
const reassortmentTargetsDisplayField: FieldType = {
    name: 'reassortmentStockTargets.[dessert/main/smallBites/starter]',
    displayInfo: '(nombre entier)',
};

const reassortmentTargetsActualFields: FieldType[] = STOCK_CATEGORIES.map((category) => ({
    name: `reassortmentStockTargets.${category}`,
    displayInfo: '(nombre entier)',
    isHidden: true,
}));

// Generate display fields for reassortment onboarding targets
const reassortmentFreshFacingsDisplayField: FieldType = {
    name: 'reassortmentFreshFacings.[dessert/main/smallBites/starter]',
    displayInfo: '(nombre entier)',
};

const reassortmentFreshFacingsActualFields: FieldType[] = STOCK_CATEGORIES.map((category) => ({
    name: `reassortmentFreshFacings.${category}`,
    displayInfo: '(nombre entier)',
    isHidden: true,
}));

const reassortmentOnboardingTargetsDisplayField: FieldType = {
    name: 'reassortmentOnboardingStockTargets.[dessert/main/smallBites/starter]',
    displayInfo: '(nombre entier)',
};

const reassortmentOnboardingTargetsActualFields: FieldType[] = STOCK_CATEGORIES.map((category) => ({
    name: `reassortmentOnboardingStockTargets.${category}`,
    displayInfo: '(nombre entier)',
    isHidden: true,
}));

// Generate display fields for reassortment stock target ratios
const reassortmentStockTargetRatiosDisplayField: FieldType = {
    name: 'reassortmentStockTargetRatios.[monday/tuesday/wednesday/thursday/friday]',
    displayInfo: '(nombre entier)',
};

const reassortmentStockTargetRatiosActualFields: FieldType[] = WEEKDAYS.map((day) => ({
    name: `reassortmentStockTargetRatios.${day}`,
    displayInfo: '(nombre entier)',
    isHidden: true,
}));

// All other fields are optional for updates
export const OPTIONAL_UPDATE_FIELDS: FieldType[] = [
    { name: 'name', displayInfo: '(nom du site)' },
    { name: 'type', displayInfo: '(BrokenProductsWarehouse, ExpiredProductsWarehouse, Microstore, Warehouse)' },
    { name: 'B2CPriceRange', displayInfo: '(ID)' },
    deliveryDaysDisplayField,
    ...deliveryDaysActualFields,
    freshReassortDaysDisplayField,
    ...freshReassortDaysActualFields,
    dryReassortDaysDisplayField,
    ...dryReassortDaysActualFields,
    { name: 'deliveryTimeWindow.start', displayInfo: '(format: HH:mm)' },
    { name: 'deliveryTimeWindow.stop', displayInfo: '(format: HH:mm)' },
    { name: 'deliveryType', displayInfo: '(WarehouseDelivery, MicrostoreDelivery, PackageDelivery)' },
    { name: 'accessTime', displayInfo: '(nombre entier)' },
    { name: 'removalDate', displayInfo: '(format: YYYY-MM-DD)' },
    { name: 'estimatedUserCount', displayInfo: '(nombre entier)' },
    { name: 'hasExternalSetupProcess', displayInfo: '(true/false)' },
    { name: 'shouldOperateOnTotops', displayInfo: '(true/false)' },
    { name: 'shouldAutomaticallyCreateTargetReassortment', displayInfo: '(true/false)' },
    { name: 'shouldAutomaticallyCreateManualReassortment', displayInfo: '(true/false)' },
    { name: 'shouldAutomaticallyFinishSiteSetup', displayInfo: '(true/false)' },
    { name: 'shouldHaveTheftNotice', displayInfo: '(true/false)' },
    { name: 'shouldModulateForExpired', displayInfo: '(true/false)' },
    { name: 'shouldModulateForUnknownLosses', displayInfo: '(true/false)' },
    { name: 'hasCoffee', displayInfo: '(true/false)' },
    { name: 'hasSensei', displayInfo: '(true/false)' },
    { name: 'useNayaxCard', displayInfo: '(true/false)' },
    { name: 'isSecured', displayInfo: '(true/false)' },
    { name: 'launchDate', displayInfo: '(format: DD/MM/YYYY)' },
    { name: 'unknownShrinkageBillingThreshold', displayInfo: '(nombre entier)' },
    // Reassortment fields with simplified display
    reassortmentFreshFacingsDisplayField,
    ...reassortmentFreshFacingsActualFields,
    reassortmentTargetsDisplayField,
    ...reassortmentTargetsActualFields,
    reassortmentOnboardingTargetsDisplayField,
    ...reassortmentOnboardingTargetsActualFields,
    reassortmentStockTargetRatiosDisplayField,
    ...reassortmentStockTargetRatiosActualFields,
    ...urbantzDisplayFields,
    ...urbantzActualFields,
];

export const ID = '_id';

export const NUMERIC_FIELDS = [
    'accessTime',
    'estimatedUserCount',
    'unknownShrinkageBillingThreshold',
    ...STOCK_CATEGORIES.map((category) => `reassortmentStockTargets.${category}`),
    ...STOCK_CATEGORIES.map((category) => `reassortmentOnboardingStockTargets.${category}`),
    ...WEEKDAYS.map((day) => `reassortmentStockTargetRatios.${day}`),
];

export const NUMERIC_FIELDS_THAT_CAN_BE_NULL = ['crossedPriceB2B', 'crossedPriceB2C'];

export const BOOLEAN_FIELDS = [
    'isSecured',
    'hasCoffee',
    'useNayaxCard',
    'hasSensei',
    'hasExternalSetupProcess',
    'shouldHaveTheftNotice',
    'shouldOperateOnTotops',
    'shouldAutomaticallyCreateTargetReassortment',
    'shouldAutomaticallyCreateManualReassortment',
    'shouldAutomaticallyFinishSiteSetup',
    'shouldModulateForExpired',
    'shouldModulateForUnknownLosses',
    ...DAYS_OF_WEEK.map((day) => `deliveryDays.${day}`),
    ...DAYS_OF_WEEK.map((day) => `freshReassortDays.${day}`),
    ...DAYS_OF_WEEK.map((day) => `dryReassortDays.${day}`),
    ...WEEKDAYS.flatMap((day) =>
        WEEK_TYPES.flatMap((weekType) =>
            URBANTZ_SERVICES.map((service) => `urbantzMetadata.${service}.${day}.${weekType}`),
        ),
    ),
];

export const ARRAY_FIELDS = [
    'urbantzMetadata.needsInventoryBoissons',
    'urbantzMetadata.needsInventorySnack',
    'urbantzMetadata.needsInventoryNonAlimentaire',
    'urbantzMetadata.needsCleaningMachineNECTA',
    'urbantzMetadata.needsSmallExpiryDateInventory',
    'urbantzMetadata.replaceCompleteInventoryByZeroInventory',
    'urbantzMetadata.needsPunctualExpiryDateInventory',
];
